/**
 * Claims API calls.
 *
 * Split out of services/dashboard.ts, which held both products' calls in one
 * 3,774-line module — the file two streams of work collided in. Nothing here
 * calls into IFAS; the two sets were fully disjoint when they were separated.
 */

import instance from "./api";
import EndPoints from "./api-endpoints";

export const getAllClaims = async (
  userId: string | undefined,
  claimType: string | null,
  page = 0,
  size = 10,
  tokenId: any,
) => {
  try {
    return instance
      .get(EndPoints.getAllClaims + "/" + userId, {
        headers: { Authorization: "Bearer " + tokenId },
        params: { claimType, page, size },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getAllFilteredClaims = async (
  status: any,
  claimType: string | null,
  page = 0,
  size = 10,
) => {
  try {
    return instance
      .get(EndPoints.getAllFilteredClaims, {
        params: { status, claimType, page, size },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const assignMe = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.assignMe, payload, {
        headers: {
          Accept: "*/*",
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const changeClaimAssignment = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.changeClaimAssignment, payload, {
        headers: { Accept: "*/*" },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const getUserRoles = async (role: any, userId: string) => {
  try {
    return instance
      .get(EndPoints.getUserRoles, {
        params: {
          role,
          userId,
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getClaimDetails = async (claimId: string, role: string) => {
  try {
    return instance
      .get(EndPoints.getClaimDetails + "/" + claimId)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getClaimHistory = async (claimId: string) => {
  try {
    return instance
      .get(EndPoints.getClaimHistory + "/" + claimId)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getTransactionHistory = async (
  policyNumber: string,
  page: number,
  size: number,
) => {
  try {
    return instance
      .get(EndPoints.paymentHistory, {
        params: { policyNumber, page, size },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getClaimNotifications = async (claimId: string) => {
  try {
    return instance
      .get(`${EndPoints.getClaimNotifications}/${claimId}/notifications`)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const claimSubmit = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.claimSubmit, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "*/*",
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const closeClaim = async (payload: any) => {
  try {
    return instance
      .post(EndPoints.closeClaim, payload)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const updateClaim = async (claimId: string, data: FormData) => {
  try {
    data.append("claimId", claimId);
    return instance
      .put(`${EndPoints.updateClaim}`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "*/*",
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const uploadDocs = async (
  claimId: string,
  data: FormData,
  deletedQuery?: string,
) => {
  try {
    const url = `${EndPoints.uploadUnderwriterDoc}?claimId=${claimId}${deletedQuery ? `&${deletedQuery}` : ""
      }`;
    return instance
      .post(url, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "*/*",
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const updateTenPercentClaim = async (data: FormData) => {
  try {
    return await instance.put(`${EndPoints.updateTenPercentClaim}`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
        Accept: "*/*",
      },
    });
  } catch (error: any) {
    return error?.response || { status: 500, message: "Server Error" };
  }
};

export const searchClaim = async (inputValue: string, claimType: string) => {
  try {
    return instance
      .get(EndPoints.searchClaim, {
        params: {
          query: inputValue,
          claimType: claimType,
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const getDocument = async (claimNumber: string | null | undefined, documentId: string) => {
  try {
    return instance
      .get(EndPoints.getDocument + claimNumber + "/" + documentId, {
        responseType: "blob", // 👈 super important
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const deleteDocument = async (
  claimOrCaseNumber: string,
  documentId: string,
) => {
  try {
    return instance
      .delete(EndPoints.deleteDocument + claimOrCaseNumber + "/" + documentId, {
        responseType: "blob",
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const generateReport = async (
  generatedBy: string,
  reportType: string,
  date: string,
) => {
  try {
    return instance
      .get(EndPoints.generateReport, {
        params: {
          generatedBy,
          reportType,
          date,
        },
      })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

// dashboard.ts (service file)

export const downloadReport = async (s3Path: string) => {
  try {
    const response = await instance.get(EndPoints.downloadReport, {
      params: { s3Path },
      responseType: "blob", // Important for file download!
      headers: {
        Accept: "text/csv",
      },
    });
    return response;
  } catch (error: any) {
    return error.response || error;
  }
};

export const getReportHistory = async (
  page: number,
  size: number,
  reportType?: string,
  date?: string,
  startDate?: string,
  endDate?: string,
  today?: boolean,
) => {
  try {
    const response = await instance.get(EndPoints.reportHistory, {
      params: {
        page,
        size,
        reportType: reportType || undefined,
        date: date || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        today: today ?? undefined,
      },
    });
    return response;
  } catch (error: any) {
    return error.response || error;
  }
};

export const uploadProofOfPayment = async (
  batchNo: string,
  userId: string,
  file: File,
) => {
  try {
    const formData = new FormData();
    formData.append("batchNo", batchNo);
    formData.append("userId", userId);
    formData.append("attachments", file);

    const response = await instance.post(EndPoints.proofOfPayment, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Accept: "*/*",
      },
    });

    return response;
  } catch (error: any) {
    return error.response || error;
  }
};

export const getClaimBenefit = async (claimId: string) => {
  try {
    return instance
      .get(`${EndPoints.claimBenefit}${claimId}/benefit-details`, {})
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error.response;
      });
  } catch (error) {
    return error;
  }
};

export const submitBenefitOverride = async (
  claimId: string,
  payload: any,
  evidence?: File,
) => {
  try {
    const formData = new FormData();
    formData.append(
      "override",
      new Blob([JSON.stringify(payload)], { type: "application/json" }),
    );
    if (evidence) {
      formData.append("evidence", evidence);
    }
    return instance
      .post(`${EndPoints.benefitOverride}/${claimId}/benefit-overrides`, formData)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error: any) {
    return error.response || error;
  }
};

export const approveBenefitOverride = async (
  claimId: string,
  overrideId: string,
  payload: any,
) => {
  try {
    return instance
      .put(
        `${EndPoints.benefitOverride}/${claimId}/benefit-overrides/${overrideId}/approve`,
        payload,
      )
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error: any) {
    return error.response || error;
  }
};

export const rejectBenefitOverride = async (
  claimId: string,
  overrideId: string,
  payload: any,
) => {
  try {
    return instance
      .put(
        `${EndPoints.benefitOverride}/${claimId}/benefit-overrides/${overrideId}/reject`,
        payload,
      )
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error: any) {
    return error.response || error;
  }
};

export const getBenefitOverrideHistory = async (claimId: string) => {
  try {
    return instance
      .get(`${EndPoints.benefitOverride}/${claimId}/benefit-overrides`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error: any) {
    return error.response || error;
  }
};

export const getBenefitInceptionContext = async (claimId: string, scheduleBenefitId: string) => {
  try {
    return instance
      .get(`${EndPoints.benefitOverride}/${claimId}/benefit-overrides/inception-context/${scheduleBenefitId}`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error: any) {
    return error.response || error;
  }
};

export const getReconciliationQueue = async (params: any) => {
  try {
    return instance
      .get(EndPoints.easipolReconciliation, { params })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error: any) {
    return error.response || error;
  }
};

export const getReconciliationSummary = async () => {
  try {
    return instance
      .get(`${EndPoints.easipolReconciliation}/summary`)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error: any) {
    return error.response || error;
  }
};

export const resolveReconciliationItem = async (id: string, payload: any) => {
  try {
    return instance
      .put(`${EndPoints.easipolReconciliation}/${id}/resolve`, payload)
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error: any) {
    return error.response || error;
  }
};

export const getMonthlyStats = async (year: number, month: number) => {
  try {
    const response = await instance.get(EndPoints.monthlyStats, {
      params: { year, month },
    });
    return response;
  } catch (error: any) {
    return error.response || error;
  }
};

export const getAgedClaimsAnalysis = async (duration?: string) => {
  try {
    const response = await instance.get(EndPoints.agedClaimsAnalysis, {
      params: { duration: duration || undefined },
    });
    return response;
  } catch (error: any) {
    return error.response || error;
  }
};

export const getDateRangeStats = async (start: string, end: string) => {
  try {
    const response = await instance.get(EndPoints.dateRangeStats, {
      params: { start, end },
    });
    return response;
  } catch (error: any) {
    return error.response || error;
  }
};

export const inFlightClaimsExecutiveRequests = new Map<string, Promise<any>>();

export const getAllClaimsData = async (
  dateType: string,
  startDate?: string,
  endDate?: string,
  claimStatus?: string,
  claimType?: string,
) => {
  const params = {
    dateType,
    reconciliationOnly: false,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    claimStatus: claimStatus || undefined,
    claimType: claimType || undefined,
  };
  const requestKey = JSON.stringify(params);
  const existingRequest = inFlightClaimsExecutiveRequests.get(requestKey);
  if (existingRequest) return existingRequest;

  const request = instance.get(EndPoints.getAllClaimsData, { params })
    .catch((error: any) => error.response || error)
    .finally(() => inFlightClaimsExecutiveRequests.delete(requestKey));
  inFlightClaimsExecutiveRequests.set(requestKey, request);

  try {
    return await request;
  } catch (error: any) {
    return error.response || error;
  }
};

export const exportClaimsReport = async (
  format: "CSV" | "EXCEL" | "PDF",
  filters: any,
) => {
  const params: any = {
    format,
    dateType: filters.dateType || "CAPTURED_DATE",
    reconciliationOnly: Boolean(filters.reconciliationOnly),
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    claimStatus: filters.claimStatus && filters.claimStatus !== "ALL" ? filters.claimStatus : undefined,
    claimType: filters.claimType && filters.claimType !== "ALL" ? filters.claimType : undefined,
  };

  const response = await instance.get(EndPoints.exportClaimsReport, {
    params,
    responseType: "blob",
  });
  return response;
};

export const getSupportingDocuments = async (claimId: string, userId: string) => {
  try {
    return instance
      .get(`${EndPoints.claim}/${claimId}/supporting-documents`, {
        params: { userId },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};

export const uploadSupportingDocument = async (claimId: string, userId: string, category: string, file: File) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    return instance
      .post(`${EndPoints.claim}/${claimId}/supporting-documents`, formData, {
        params: { userId, category },
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => response)
      .catch((error) => error.response);
  } catch (error) {
    return error;
  }
};
